/**
 * usePricingVersions Hook
 *
 * CRUD operations for pricing_versions and pricing_version_tiers tables.
 * Trainers create pricing versions (e.g. "2025 Pricing") with per-package-size
 * tiers. Clients are assigned a version so rates auto-populate during reup.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/hooks/use-organization";
import { useToast } from "@/hooks/use-toast";

export interface PricingVersionTier {
  id: string;
  pricing_version_id: string;
  package_size: number;
  price_cents: number;
  created_at: string;
}

export interface PricingVersion {
  id: string;
  user_id: string;
  organization_id: string | null;
  name: string;
  is_default: boolean;
  tiers: PricingVersionTier[];
  created_at: string;
  updated_at: string;
}

export interface TierInput {
  package_size: number;
  price_cents: number;
}

export function usePricingVersions() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { toast } = useToast();
  const [versions, setVersions] = useState<PricingVersion[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVersions = useCallback(async () => {
    if (!user) {
      setVersions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("pricing_versions")
        .select("*, pricing_version_tiers(*)")
        .order("created_at", { ascending: true });

      if (error) throw error;

      const mapped: PricingVersion[] = (data || []).map((v: any) => ({
        id: v.id,
        user_id: v.user_id,
        organization_id: v.organization_id,
        name: v.name,
        is_default: v.is_default,
        tiers: (v.pricing_version_tiers || []).sort(
          (a: PricingVersionTier, b: PricingVersionTier) => a.package_size - b.package_size
        ),
        created_at: v.created_at,
        updated_at: v.updated_at,
      }));

      setVersions(mapped);
    } catch (err) {
      console.error("Error fetching pricing versions:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const createVersion = useCallback(
    async (name: string, tiers: TierInput[], isDefault = false) => {
      if (!user) return null;

      try {
        // If setting as default, unset existing default first
        if (isDefault) {
          await supabase
            .from("pricing_versions")
            .update({ is_default: false })
            .eq("user_id", user.id)
            .eq("is_default", true);
        }

        const { data: version, error: vError } = await supabase
          .from("pricing_versions")
          .insert({
            user_id: user.id,
            organization_id: organization?.id || null,
            name,
            is_default: isDefault,
          })
          .select()
          .single();

        if (vError) throw vError;

        if (tiers.length > 0) {
          const { error: tError } = await supabase
            .from("pricing_version_tiers")
            .insert(
              tiers.map((t) => ({
                pricing_version_id: version.id,
                package_size: t.package_size,
                price_cents: t.price_cents,
              }))
            );

          if (tError) throw tError;
        }

        toast({ title: "Pricing version created", description: name });
        await fetchVersions();
        return version.id;
      } catch (err: any) {
        toast({
          title: "Error creating pricing version",
          description: err.message,
          variant: "destructive",
        });
        return null;
      }
    },
    [user, organization, toast, fetchVersions]
  );

  const updateVersion = useCallback(
    async (id: string, name: string, tiers: TierInput[]) => {
      if (!user) return false;

      try {
        const { error: vError } = await supabase
          .from("pricing_versions")
          .update({ name })
          .eq("id", id);

        if (vError) throw vError;

        // Replace tiers: delete existing, insert new
        await supabase
          .from("pricing_version_tiers")
          .delete()
          .eq("pricing_version_id", id);

        if (tiers.length > 0) {
          const { error: tError } = await supabase
            .from("pricing_version_tiers")
            .insert(
              tiers.map((t) => ({
                pricing_version_id: id,
                package_size: t.package_size,
                price_cents: t.price_cents,
              }))
            );

          if (tError) throw tError;
        }

        toast({ title: "Pricing version updated" });
        await fetchVersions();
        return true;
      } catch (err: any) {
        toast({
          title: "Error updating pricing version",
          description: err.message,
          variant: "destructive",
        });
        return false;
      }
    },
    [user, toast, fetchVersions]
  );

  const deleteVersion = useCallback(
    async (id: string) => {
      try {
        const { error } = await supabase
          .from("pricing_versions")
          .delete()
          .eq("id", id);

        if (error) throw error;

        toast({ title: "Pricing version deleted" });
        await fetchVersions();
        return true;
      } catch (err: any) {
        toast({
          title: "Error deleting pricing version",
          description: err.message,
          variant: "destructive",
        });
        return false;
      }
    },
    [toast, fetchVersions]
  );

  const setDefault = useCallback(
    async (id: string) => {
      if (!user) return false;

      try {
        // Unset existing default
        await supabase
          .from("pricing_versions")
          .update({ is_default: false })
          .eq("user_id", user.id)
          .eq("is_default", true);

        // Set new default
        const { error } = await supabase
          .from("pricing_versions")
          .update({ is_default: true })
          .eq("id", id);

        if (error) throw error;

        toast({ title: "Default pricing version updated" });
        await fetchVersions();
        return true;
      } catch (err: any) {
        toast({
          title: "Error setting default",
          description: err.message,
          variant: "destructive",
        });
        return false;
      }
    },
    [user, toast, fetchVersions]
  );

  const getDefaultVersion = useCallback((): PricingVersion | null => {
    return versions.find((v) => v.is_default) || null;
  }, [versions]);

  const getVersionById = useCallback(
    (id: string | null): PricingVersion | null => {
      if (!id) return null;
      return versions.find((v) => v.id === id) || null;
    },
    [versions]
  );

  const getClientVersion = useCallback(
    (clientPricingVersionId: string | null): PricingVersion | null => {
      // Use client's assigned version, or fall back to default
      if (clientPricingVersionId) {
        const assigned = versions.find((v) => v.id === clientPricingVersionId);
        if (assigned) return assigned;
      }
      return getDefaultVersion();
    },
    [versions, getDefaultVersion]
  );

  return {
    versions,
    loading,
    createVersion,
    updateVersion,
    deleteVersion,
    setDefault,
    getDefaultVersion,
    getVersionById,
    getClientVersion,
    reload: fetchVersions,
  };
}
